import { AnimatePresence, motion } from 'framer-motion'
import { light } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useEffect, useMemo } from 'react'
import { secondsToMilliseconds } from 'date-fns'
import { useTagData } from 'tags/api'
import {
  Button,
  Text,
  TimePickerInput,
  TimeSeriesChart,
} from 'src/components/ui'
import { isDefined, TimeRangeOrPeriod } from 'src/types'
import useTimeRange, { TimeRangeProvider } from 'src/contexts/timeRange'
import { timeRangeOrPeriodToTimeRange } from 'src/utility/time'
import { ExcludedPeriod } from '../ExcludedPeriod'
import { useTrainingPeriodStore } from '../../training-period.state'

type AdvancedTrainingPeriodProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  tagId: string
  includedTimeRange: TimeRangeOrPeriod
}

export function AdvancedTrainingPeriodWithProvider({
  isOpen,
  tagId,
  includedTimeRange,
}: AdvancedTrainingPeriodProps): JSX.Element {
  const {
    excludedPeriods,
    editExcludedPeriod,
    removeExcludedPeriod,
    addExcludedPeriod,
  } = useTrainingPeriodStore()
  const { timeRange, setTimeRange } = useTimeRange()
  const tagData = useTagData({ id: tagId })

  useEffect(() => {
    if (isOpen) {
      setTimeRange(timeRangeOrPeriodToTimeRange(includedTimeRange))
    }
  }, [includedTimeRange, isOpen, setTimeRange])

  const excludedTimeRanges = useMemo(
    () => excludedPeriods.filter(({ hidden }) => !hidden),
    [excludedPeriods],
  )

  function handleTimeRangeChange(
    updatedTimeRangeOrPeriod: TimeRangeOrPeriod,
  ): void {
    const updatedTimeRange = timeRangeOrPeriodToTimeRange(
      updatedTimeRangeOrPeriod,
    )
    setTimeRange(updatedTimeRange)
  }

  function handleTimeRangeReset(): void {
    setTimeRange(timeRangeOrPeriodToTimeRange(includedTimeRange))
  }

  const includedTime = timeRangeOrPeriodToTimeRange(includedTimeRange)
  const isTimeRangeModified =
    Math.abs(includedTime.from - timeRange.from) > secondsToMilliseconds(10) ||
    Math.abs(includedTime.to - timeRange.to) > secondsToMilliseconds(10)

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          onClick={e => e.stopPropagation()}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.1 }}
          className="flex flex-col gap-xs"
        >
          <hr className="mt-m h-px w-full bg-border" />
          <div>
            <Text variant="description" bold>
              Excluded time period
            </Text>
            <Text variant="description">
              The AI model will focus less on this period
            </Text>
          </div>
          <div className="flex flex-col items-start gap-s">
            {excludedPeriods.map(period => (
              <ExcludedPeriod
                key={period.id}
                value={period.timeRange}
                hidden={period.hidden}
                onTimeRangeChange={timeRange =>
                  editExcludedPeriod(period.id, {
                    timeRange,
                  })
                }
                onHiddenChange={hidden =>
                  editExcludedPeriod(period.id, {
                    hidden,
                  })
                }
                remove={() => removeExcludedPeriod(period.id)}
              />
            ))}
            <div className="flex w-full items-center justify-between">
              <Button
                disabled={excludedPeriods.some(p => !p.timeRange)}
                variant="icon-secondary"
                icon={light('plus')}
                onClick={() => addExcludedPeriod()}
                title="Exclude Time Period"
              />
              <div className="flex items-center gap-xs">
                {isTimeRangeModified && (
                  <Button
                    variant="tertiary"
                    onClick={handleTimeRangeReset}
                    title="Reset Time Range"
                  />
                )}
                <TimePickerInput
                  value={timeRange}
                  isLive={false}
                  maxDate={new Date()}
                  minDate={new Date(timeRange.from)}
                  onChange={handleTimeRangeChange}
                />
              </div>
            </div>
          </div>
          <div className="mt-s">
            <TimeSeriesChart
              data={[{ data: tagData.data }]}
              isPending={tagData.isLoading}
              tooltip
              excludedTimePeriods={excludedTimeRanges
                .map(({ timeRange }) => timeRange)
                .filter(isDefined)}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export function AdvancedTrainingPeriod(
  props: AdvancedTrainingPeriodProps,
): JSX.Element {
  return (
    <TimeRangeProvider>
      <AdvancedTrainingPeriodWithProvider {...props} />
    </TimeRangeProvider>
  )
}
